import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { chatGetSessionQueryKey, editWithAiMutation, errorMessage } from "@/api";
import { buildHost } from "@/features/chat/chatHost";
import { fetchDefaultChatModelId } from "@/features/chat/useChatModels";
import { chatSessionRequest } from "@/features/chat/useChatSession";
import type { BuildEntity } from "./examplePrompts";

/**
 * "Edit with AI": the chat that built the entity when it's mine, otherwise a new build chat opened for it.
 * The assistant sees the entity's current configuration there (the server decides which chat, MHS-733).
 */
export function useEditWithAi(entityType: BuildEntity, uuid: string) {
  const { t } = useTranslation("build", { keyPrefix: "editWithAi" });
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const open = useMutation({
    ...editWithAiMutation(),
    onSuccess: (result) => {
      const key = chatGetSessionQueryKey(chatSessionRequest(result.data.uuid));
      queryClient.setQueryData(key, result);
      navigate(buildHost.pathFor(result.data.uuid));
    },
    onError: (error) => toast.error(errorMessage(error, t("failed"))),
  });
  return {
    busy: open.isPending,
    open: async () => {
      // A new build chat starts on the default model; resolved on click so detail pages don't load models.
      const modelId = await fetchDefaultChatModelId(queryClient).catch(() => null);
      if (!modelId) return toast.error(t("noModel"));
      open.mutate({ body: { entityType, uuid, modelId } });
    },
  };
}
